import type { PluginDto, PluginType } from '../dto/pluginDto';
import type { PluginConfig, TenantConfigDto } from '../dto/tenantModel';
import { createUuid } from './createUuid';

interface LegacyPluginMapping {
  type: PluginType;
  urlProperty: 'metadata' | 'timeline' | 'activityPluginUrl';
  titleProperty?: 'metadataTitle' | 'activityPluginTitle';
  alwaysReloadProperty?: 'alwaysReloadMetadataPlugin';
}

export const LEGACY_PLUGIN_MAPPINGS: LegacyPluginMapping[] = [
  {
    type: 'metadata',
    urlProperty: 'metadata',
    titleProperty: 'metadataTitle',
    alwaysReloadProperty: 'alwaysReloadMetadataPlugin',
  },
  {
    type: 'timeline',
    urlProperty: 'timeline',
  },
  {
    type: 'systemActivity',
    urlProperty: 'activityPluginUrl',
    titleProperty: 'activityPluginTitle',
  },
];

export const migratePluginToNewFormat = (
  tenantConfig: Pick<TenantConfigDto, 'pluginConfig'>,
  pluginConfig: PluginConfig,
  generateId: boolean = false,
): PluginDto[] => {
  const existingPlugins = tenantConfig.pluginConfig ?? [];
  const newPlugins: PluginDto[] = [];

  for (const mapping of LEGACY_PLUGIN_MAPPINGS) {
    const url = pluginConfig[mapping.urlProperty];

    if (!url) {
      continue;
    }

    // Check if plugin already exists
    const pluginAlreadyExists = existingPlugins.some(
      plugin => plugin.type === mapping.type && plugin.url === url
    );

    if (!pluginAlreadyExists) {
      const title = mapping.titleProperty
        ? pluginConfig[mapping.titleProperty]
        : undefined;

      const alwaysReload = mapping.alwaysReloadProperty
        ? pluginConfig[mapping.alwaysReloadProperty]
        : undefined;

      newPlugins.push({
        id: generateId ? createUuid() : `temp-id-${createUuid()}`,
        type: mapping.type,
        url,
        ...(title && { title }),
        ...(alwaysReload !== undefined && { alwaysReload: !!alwaysReload }),
        default: true,
      });
    }
  }

  return [...existingPlugins, ...newPlugins];

};

export const getLegacyPluginObject = (plugin: PluginDto, currentPluginConfig: PluginConfig | undefined, deleted: boolean = false): PluginConfig => {
  const legacyPluginConfig: PluginConfig = { ...currentPluginConfig };

  const mapping = LEGACY_PLUGIN_MAPPINGS.find(m => m.type === plugin.type);
  if (!mapping) {
    return legacyPluginConfig;
  }

  if (!deleted) {
    legacyPluginConfig[mapping.urlProperty] = plugin.url;

    if (mapping.titleProperty) {
      legacyPluginConfig[mapping.titleProperty] = plugin.title || null;
    }
    if (mapping.alwaysReloadProperty) {
      legacyPluginConfig[mapping.alwaysReloadProperty] = plugin.alwaysReload !== undefined ? plugin.alwaysReload : null;
    }

    return legacyPluginConfig;
  }

  // Delete the mapped properties when plugin is deleted
  delete legacyPluginConfig[mapping.urlProperty];
  if (mapping.titleProperty) {
    delete legacyPluginConfig[mapping.titleProperty];
  }
  if (mapping.alwaysReloadProperty) {
    delete legacyPluginConfig[mapping.alwaysReloadProperty];
  }
  return legacyPluginConfig;
};
